// /app/frontend/src/components/frugal-v2/components/reports/ClosingDetailView.jsx
// Detail view for a single closed month (shows all sections from snapshot)

import React from 'react';
import { Download, Info } from 'lucide-react';

const fmt = (n) => `$${(typeof n === 'number' ? n : 0).toFixed(2)}`;

const Stat = ({ label, value, tone = 'slate' }) => {
  const toneCls = {
    emerald: 'text-emerald-300',
    rose: 'text-rose-300',
    sky: 'text-sky-300',
    amber: 'text-amber-300',
    purple: 'text-purple-300',
    slate: 'text-slate-200'
  }[tone];
  return (
    <div className="bg-white/5 rounded-xl px-3 py-2.5">
      <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
      <p className={`text-base font-semibold ${toneCls}`}>{value}</p>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-1">{title}</h3>
    {children}
  </div>
);

const RowList = ({ items }) => (
  <ul className="bg-white/5 rounded-xl divide-y divide-white/5">
    {items.map((it, idx) => (
      <li key={idx} className="px-3.5 py-2.5 flex items-center justify-between gap-2">
        <span className="text-sm text-slate-200 truncate flex-1">{it.label}</span>
        <span className={`text-sm font-medium ${it.tone || 'text-slate-100'}`}>{it.value}</span>
      </li>
    ))}
  </ul>
);

const ClosingDetailView = ({ closing, t, onDownload }) => {
  const summary = closing?.summary || {};
  const cats = Array.isArray(summary.categories) ? summary.categories : [];
  const saved = (summary.total_budget || 0) - (summary.total_spent || 0);
  const isPartial = !summary.schema_version || summary.schema_version < 2;

  const income = summary.income;
  const savings = summary.savings;
  const accounts = summary.accounts;
  const vital = summary.vital;
  const contingency = summary.contingency;
  const comparison = summary.previous_month_comparison;

  return (
    <div className="px-5 py-4 space-y-4">
      {isPartial && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <Info className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-200">
            <p className="font-medium">{t('frugal.reportsHistory.partialReport')}</p>
            <p className="text-amber-300/70 mt-0.5">{t('frugal.reportsHistory.onlyCategoriesAvailable')}</p>
          </div>
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3">
        <Stat label={t('frugal.reportsHistory.budget')} value={fmt(summary.total_budget)} tone="slate" />
        <Stat label={t('frugal.reportsHistory.spent')} value={fmt(summary.total_spent)} tone="rose" />
        <Stat
          label={t('frugal.reportsHistory.saved')}
          value={fmt(saved)}
          tone={saved >= 0 ? 'emerald' : 'rose'}
        />
        <Stat
          label={t('frugal.reportsHistory.transactions')}
          value={String(summary.transactions_count || 0)}
          tone="slate"
        />
      </div>

      {/* Income */}
      {income && (
        <Section title={t('frugal.reportsHistory.income')}>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('frugal.reportsHistory.budget')} value={fmt(income.total)} tone="slate" />
            <Stat label={t('frugal.reportsHistory.incomeReceived')} value={fmt(income.received)} tone="emerald" />
            <Stat label={t('frugal.reportsHistory.incomePending')} value={fmt(income.pending)} tone="amber" />
          </div>
        </Section>
      )}

      {/* Savings */}
      {savings && (
        <Section title={t('frugal.reportsHistory.savings')}>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('frugal.reportsHistory.budget')} value={fmt(savings.total)} tone="slate" />
            <Stat label={t('frugal.reportsHistory.savingsExecuted')} value={fmt(savings.saved)} tone="emerald" />
            <Stat label={t('frugal.reportsHistory.incomePending')} value={fmt(savings.pending)} tone="amber" />
          </div>
        </Section>
      )}

      {/* Banks & Cards & Receivables & Payables */}
      {accounts && (
        accounts.banks?.length > 0 ||
        accounts.cards?.length > 0 ||
        accounts.receivables?.length > 0 ||
        accounts.payables?.length > 0
      ) && (
        <Section title={t('frugal.reportsHistory.banksCards')}>
          <RowList
            items={[
              ...(accounts.banks || []).map((b) => ({
                label: `${t('frugal.reportsHistory.bank')}: ${b.name}`,
                value: fmt(b.balance),
                tone: 'text-emerald-300'
              })),
              ...(accounts.cards || []).map((c) => ({
                label: `${t('frugal.reportsHistory.card')}: ${c.name}`,
                value: fmt(c.balance),
                tone: 'text-rose-300'
              })),
              ...(accounts.receivables || []).map((r) => ({
                label: `Receivable: ${r.name}`,
                value: fmt(r.balance),
                tone: 'text-sky-300'
              })),
              ...(accounts.payables || []).map((p) => ({
                label: `Payable: ${p.name}`,
                value: fmt(-p.balance),
                tone: 'text-orange-300'
              })),
              {
                label: t('frugal.reportsHistory.netPosition'),
                value: fmt(accounts.net_position),
                tone: accounts.net_position >= 0 ? 'text-emerald-300' : 'text-rose-300'
              },
              {
                label: t('frugal.accounts.totalBalance', 'Total Balance'),
                value: fmt(accounts.total_balance ?? accounts.net_position),
                tone: (accounts.total_balance ?? accounts.net_position) >= 0
                  ? 'text-emerald-300 font-bold'
                  : 'text-rose-300 font-bold'
              }
            ]}
          />
        </Section>
      )}

      {/* Vital reserve */}
      {vital && vital.total_budget > 0 && (
        <Section title={t('frugal.reportsHistory.vitalReserve')}>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('frugal.reportsHistory.budget')} value={fmt(vital.total_budget)} tone="slate" />
            <Stat label={t('frugal.reportsHistory.spent')} value={fmt(vital.total_spent)} tone="rose" />
            <Stat label={t('frugal.reportsHistory.vitalRemaining')} value={fmt(vital.remaining)} tone={vital.remaining >= 0 ? 'emerald' : 'rose'} />
          </div>
        </Section>
      )}

      {/* Contingency */}
      {contingency && contingency.used > 0 && (
        <Section title={t('frugal.reportsHistory.contingency')}>
          <div className="grid grid-cols-2 gap-2">
            <Stat label={t('frugal.reportsHistory.contingencyUsed')} value={fmt(contingency.used)} tone="amber" />
            <Stat label={t('frugal.reportsHistory.transactions')} value={String(contingency.records_count || 0)} tone="slate" />
          </div>
        </Section>
      )}

      {/* Comparison */}
      {comparison?.totals && (comparison.totals.previous > 0 || comparison.totals.current > 0) && (
        <Section title={t('frugal.reportsHistory.comparison')}>
          <div className="grid grid-cols-3 gap-2">
            <Stat label={t('frugal.reportsHistory.current')} value={fmt(comparison.totals.current)} tone="purple" />
            <Stat label={t('frugal.reportsHistory.previous')} value={fmt(comparison.totals.previous)} tone="sky" />
            <Stat
              label={t('frugal.reportsHistory.diff')}
              value={fmt(comparison.totals.diff)}
              tone={(comparison.totals.diff || 0) <= 0 ? 'emerald' : 'rose'}
            />
          </div>
        </Section>
      )}

      {/* Categories */}
      {cats.length > 0 && (
        <Section title={t('frugal.reportsHistory.categories')}>
          <ul className="bg-white/5 rounded-xl divide-y divide-white/5">
            {cats.map((c) => (
              <li key={c.category} className="px-3.5 py-2.5 flex items-center justify-between">
                <span className="text-sm text-slate-200 truncate flex-1">{c.category}</span>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">{fmt(c.budgeted)}</span>
                  <span className="text-sm font-medium text-rose-300">{fmt(c.spent)}</span>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Download CTA */}
      <button
        onClick={(e) => onDownload(closing, e)}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-purple-500/15 hover:bg-purple-500/25 text-purple-200 font-medium transition-colors"
        data-testid="reports-history-detail-pdf"
      >
        <Download className="w-4 h-4" />
        {t('frugal.reportsHistory.downloadPdf')}
      </button>
    </div>
  );
};

export default ClosingDetailView;
